import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, useToast, PhoneInput } from '@caruso/ui';
import {
  companiesService,
  CreateCompanyDto,
  UpdateCompanyDto,
  CompanyEmployee,
  CreateCompanyEmployeeDto
} from '@/services/companies.service';
import { clientsService } from '@/services/clients.service';
import { positionsService } from '@/services/positions.service';
import { ArrowLeft, Save, Search, MapPin, Users, Plus, Trash2, User, Briefcase, Lock, Mail } from 'lucide-react';
import InputMask from 'react-input-mask';
import { cn } from '@caruso/ui';
import 'react-phone-number-input/style.css';

const companySchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  cnpj: z.string().optional(),
  mainPhone: z.string().optional(),
  zipCode: z.string().optional(),
  address: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

export function CompanyFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = !!id;

  // Sub-tab state for the content area
  const [activeSubTab, setActiveSubTab] = useState<'endereco' | 'colaboradores'>('endereco');

  // CEP state
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  // Employee state
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [searchClient, setSearchClient] = useState('');
  const [debouncedSearchClient, setDebouncedSearchClient] = useState('');
  const [searchPosition, setSearchPosition] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<any>(null);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [filteredPositions, setFilteredPositions] = useState<any[]>([]);
  const [employeeNotes, setEmployeeNotes] = useState('');
  const [employeeStartDate, setEmployeeStartDate] = useState('');
  const [createUserLogin, setCreateUserLogin] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
  });

  const watchZipCode = watch('zipCode');
  const watchMainPhone = watch('mainPhone');

  // Load company data if editing
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', id],
    queryFn: () => companiesService.getById(id!),
    enabled: isEdit,
  });

  // Load employees
  const {
    data: employees = [],
    isLoading: isLoadingEmployees,
    refetch: refetchEmployees
  } = useQuery({
    queryKey: ['company-employees', id],
    queryFn: () => companiesService.getEmployees(id!),
    enabled: isEdit && activeSubTab === 'colaboradores',
  });

  // Load clients dynamically based on search
  const { data: clientsData, isLoading: isLoadingClients } = useQuery({
    queryKey: ['clients-search', debouncedSearchClient],
    queryFn: () => clientsService.getAll({
      page: 1,
      pageSize: 50,
      search: debouncedSearchClient || undefined
    }),
    enabled: debouncedSearchClient.length >= 2,
  });

  // Load positions for dropdown (only corporate positions)
  const { data: positionsData } = useQuery({
    queryKey: ['positions-corporate'],
    queryFn: () => positionsService.getAll({ page: 1, pageSize: 100, type: 'corporate' }),
  });

  // Set form values when editing
  useEffect(() => {
    if (company) {
      // Formata o telefone para E.164 se necessário
      let formattedPhone = company.mainPhone || '';
      if (formattedPhone && !formattedPhone.startsWith('+')) {
        const cleaned = formattedPhone.replace(/\D/g, '');
        if (cleaned.length === 10 || cleaned.length === 11) {
          formattedPhone = `+55${cleaned}`;
        } else if (cleaned.length > 11) {
          formattedPhone = `+${cleaned}`;
        }
      }

      reset({
        name: company.name,
        cnpj: company.cnpj || '',
        mainPhone: formattedPhone,
        zipCode: company.zipCode || '',
        address: company.address || '',
        number: company.number || '',
        complement: company.complement || '',
        neighborhood: company.neighborhood || '',
        city: company.city || '',
        state: company.state || '',
      });
    }
  }, [company, reset]);

  // CEP Search
  useEffect(() => {
    const cep = watchZipCode?.replace(/\D/g, '');
    if (cep && cep.length === 8) {
      handleCepSearch(cep);
    }
  }, [watchZipCode]);

  // Debounce client search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchClient(searchClient);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchClient]);

  // Update filtered clients from API results
  useEffect(() => {
    if (clientsData?.data && searchClient) {
      setFilteredClients(clientsData.data);
      setShowClientDropdown(clientsData.data.length > 0);
    } else {
      setFilteredClients([]);
      setShowClientDropdown(false);
    }
  }, [clientsData, searchClient]);

  // Search positions effect
  useEffect(() => {
    if (searchPosition && positionsData?.data) {
      const filtered = positionsData.data.filter(position =>
        position.name.toLowerCase().includes(searchPosition.toLowerCase()) ||
        position.department?.toLowerCase().includes(searchPosition.toLowerCase())
      );
      setFilteredPositions(filtered);
      setShowPositionDropdown(filtered.length > 0);
    } else {
      setFilteredPositions([]);
      setShowPositionDropdown(false);
    }
  }, [searchPosition, positionsData]);

  const handleCepSearch = async (cep: string) => {
    setIsLoadingCep(true);
    try {
      const address = await companiesService.searchAddress(cep);
      if (address) {
        setValue('address', address.street || '');
        setValue('neighborhood', address.neighborhood || '');
        setValue('city', address.city || '');
        setValue('state', address.state || '');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setIsLoadingCep(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateCompanyDto) => companiesService.create(data),
    onSuccess: () => {
      toast({
        title: 'Empresa criada com sucesso',
        variant: 'success',
      });
      navigate('/app/empresas');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar empresa',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCompanyDto) => companiesService.update(id!, data),
    onSuccess: () => {
      toast({
        title: 'Empresa atualizada com sucesso',
        variant: 'success',
      });
      navigate('/app/empresas');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar empresa',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: CompanyFormData) => {
    // Remove campos vazios para enviar como undefined
    const cleanData = {
      name: data.name,
      cnpj: data.cnpj?.trim() || undefined,
      mainPhone: data.mainPhone?.trim() || undefined,
      zipCode: data.zipCode?.trim() || undefined,
      address: data.address?.trim() || undefined,
      number: data.number?.trim() || undefined,
      complement: data.complement?.trim() || undefined,
      neighborhood: data.neighborhood?.trim() || undefined,
      city: data.city?.trim() || undefined,
      state: data.state?.trim() || undefined,
    };

    if (isEdit) {
      updateMutation.mutate(cleanData);
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const handleAddEmployee = async () => {
    if (!selectedClient || !selectedPosition) {
      toast({
        title: 'Selecione uma pessoa e um cargo',
        variant: 'destructive',
      });
      return;
    }

    if (createUserLogin) {
      if (!userEmail || !userPassword) {
        toast({
          title: 'Email e senha são obrigatórios',
          description: 'Preencha o email e a senha para criar o usuário de login',
          variant: 'destructive',
        });
        return;
      }
      if (userPassword.length < 6) {
        toast({
          title: 'Senha muito curta',
          description: 'A senha deve ter no mínimo 6 caracteres',
          variant: 'destructive',
        });
        return;
      }
    }

    if (!id) return;

    try {
      const data: CreateCompanyEmployeeDto = {
        clientId: selectedClient.id,
        positionId: selectedPosition.id,
        startDate: employeeStartDate || undefined,
        notes: employeeNotes || undefined,
        active: true,
        createUser: createUserLogin,
        userEmail: createUserLogin ? userEmail : undefined,
        userPassword: createUserLogin ? userPassword : undefined,
      };

      await companiesService.addEmployee(id, data);
      toast({
        title: 'Colaborador adicionado com sucesso',
        variant: 'success',
      });
      refetchEmployees();

      // Reset form
      setSelectedClient(null);
      setSearchClient('');
      setSelectedPosition(null);
      setSearchPosition('');
      setEmployeeNotes('');
      setEmployeeStartDate('');
      setCreateUserLogin(false);
      setUserEmail('');
      setUserPassword('');
      setIsAddingEmployee(false);
    } catch (error: any) {
      toast({
        title: 'Erro ao adicionar colaborador',
        description: error.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveEmployee = async (employeeId: string) => {
    if (!id) return;

    if (!confirm('Tem certeza que deseja remover este colaborador?')) {
      return;
    }

    try {
      await companiesService.removeEmployee(id, employeeId);
      toast({
        title: 'Colaborador removido com sucesso',
        variant: 'success',
      });
      refetchEmployees();
    } catch (error) {
      toast({
        title: 'Erro ao remover colaborador',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingCompany) {
    return <div className="text-center py-12 text-brand-ivory/50">Carregando...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/app/empresas')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-3xl font-serif text-brand-gold">
          {isEdit ? 'Editar Empresa' : 'Nova Empresa'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Information */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Informações da Empresa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-brand-ivory">
                  Nome *
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
                {errors.name && (
                  <p className="text-xs text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cnpj" className="text-brand-ivory">
                  CNPJ
                </Label>
                <InputMask
                  mask="99.999.999/9999-99"
                  {...register('cnpj')}
                >
                  {(inputProps: any) => (
                    <Input
                      {...inputProps}
                      id="cnpj"
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      placeholder="00.000.000/0000-00"
                    />
                  )}
                </InputMask>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mainPhone" className="text-brand-ivory">
                  Telefone
                </Label>
                <PhoneInput
                  id="mainPhone"
                  value={watchMainPhone}
                  onChange={(value) => setValue('mainPhone', value || '')}
                  defaultCountry="BR"
                  placeholder="Digite o telefone"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sub-tabs for Address and Employees */}
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <div className="flex gap-4 border-b border-brand-gold/20">
              <button
                type="button"
                onClick={() => setActiveSubTab('endereco')}
                className={cn(
                  'pb-2 px-1 text-sm font-medium transition-colors relative',
                  activeSubTab === 'endereco'
                    ? 'text-brand-gold'
                    : 'text-brand-ivory/50 hover:text-brand-ivory'
                )}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Endereço
                </span>
                {activeSubTab === 'endereco' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold" />
                )}
              </button>
              {isEdit && (
                <button
                  type="button"
                  onClick={() => setActiveSubTab('colaboradores')}
                  className={cn(
                    'pb-2 px-1 text-sm font-medium transition-colors relative',
                    activeSubTab === 'colaboradores'
                      ? 'text-brand-gold'
                      : 'text-brand-ivory/50 hover:text-brand-ivory'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Colaboradores
                  </span>
                  {activeSubTab === 'colaboradores' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-gold" />
                  )}
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Address Sub-tab Content */}
            {activeSubTab === 'endereco' && (
              <div className="space-y-4">
                {/* CEP */}
                <div>
                  <Label htmlFor="zipCode" className="text-brand-ivory">
                    CEP
                  </Label>
                  <div className="relative">
                    <InputMask
                      mask="99999-999"
                      {...register('zipCode')}
                    >
                      {(inputProps: any) => (
                        <Input
                          {...inputProps}
                          id="zipCode"
                          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pr-10"
                          placeholder="00000-000"
                        />
                      )}
                    </InputMask>
                    {isLoadingCep && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Search className="w-4 h-4 text-brand-gold animate-pulse" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Digite o CEP para preencher o endereço automaticamente
                  </p>
                </div>

                {/* Address Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-brand-ivory">
                      Endereço
                    </Label>
                    <Input
                      id="address"
                      {...register('address')}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="number" className="text-brand-ivory">
                      Número
                    </Label>
                    <Input
                      id="number"
                      {...register('number')}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="complement" className="text-brand-ivory">
                      Complemento
                    </Label>
                    <Input
                      id="complement"
                      {...register('complement')}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="neighborhood" className="text-brand-ivory">
                      Bairro
                    </Label>
                    <Input
                      id="neighborhood"
                      {...register('neighborhood')}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-brand-ivory">
                      Cidade
                    </Label>
                    <Input
                      id="city"
                      {...register('city')}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-brand-ivory">
                      Estado
                    </Label>
                    <Input
                      id="state"
                      {...register('state')}
                      className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      maxLength={2}
                      placeholder="UF"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Collaborators Sub-tab Content */}
            {activeSubTab === 'colaboradores' && isEdit && (
              <div className="space-y-4">
                {/* Add Employee Button */}
                {!isAddingEmployee && (
                  <Button
                    type="button"
                    onClick={() => setIsAddingEmployee(true)}
                    className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Colaborador
                  </Button>
                )}

                {/* Add Employee Form */}
                {isAddingEmployee && (
                  <div className="border border-brand-gold/20 rounded-lg p-4 space-y-4 bg-brand-smoke/30">
                    <h4 className="text-brand-gold font-medium">Novo Colaborador</h4>

                    {/* Person Search */}
                    <div className="relative">
                      <Label className="text-brand-ivory">Pessoa</Label>
                      <div className="relative mt-2">
                        <Input
                          value={selectedClient ? selectedClient.name : searchClient}
                          onChange={(e) => {
                            setSearchClient(e.target.value);
                            setSelectedClient(null);
                          }}
                          placeholder="Buscar pessoa..."
                          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                        />
                        <User className="w-4 h-4 text-brand-gold/50 absolute left-3 top-1/2 -translate-y-1/2" />

                        {showClientDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-brand-ink border border-brand-gold/20 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredClients.map(client => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setSelectedClient(client);
                                  setSearchClient(client.name);
                                  setShowClientDropdown(false);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-brand-gold/10 text-brand-ivory"
                              >
                                <div>
                                  <div className="font-medium">{client.name}</div>
                                  {client.email && (
                                    <div className="text-xs text-brand-ivory/50">{client.email}</div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Position Input */}
                    <div>
                      <Label htmlFor="position" className="text-brand-ivory">Cargo</Label>
                      <div className="relative mt-2">
                        <Search className="w-4 h-4 text-brand-gold/50 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                        <Input
                          id="position"
                          value={selectedPosition ? selectedPosition.name : searchPosition}
                          onChange={(e) => {
                            setSearchPosition(e.target.value);
                            setSelectedPosition(null);
                          }}
                          placeholder="Buscar cargo..."
                          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                        />
                        {showPositionDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-brand-ink border border-brand-gold/20 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredPositions.map(position => (
                              <button
                                key={position.id}
                                type="button"
                                onClick={() => {
                                  setSelectedPosition(position);
                                  setSearchPosition(position.name);
                                  setShowPositionDropdown(false);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-brand-gold/10 text-brand-ivory"
                              >
                                <div>
                                  <div className="font-medium">{position.name}</div>
                                  {position.department && (
                                    <div className="text-xs text-brand-ivory/50">{position.department}</div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Start Date */}
                    <div>
                      <Label htmlFor="startDate" className="text-brand-ivory">
                        Data de Início
                      </Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={employeeStartDate}
                        onChange={(e) => setEmployeeStartDate(e.target.value)}
                        className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      />
                    </div>

                    {/* Notes */}
                    <div>
                      <Label htmlFor="notes" className="text-brand-ivory">
                        Observações
                      </Label>
                      <Input
                        id="notes"
                        value={employeeNotes}
                        onChange={(e) => setEmployeeNotes(e.target.value)}
                        placeholder="Observações sobre o colaborador..."
                        className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                      />
                    </div>

                    {/* User Creation Section */}
                    <div className="border-t border-brand-gold/20 pt-4 space-y-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="createUser"
                          checked={createUserLogin}
                          onChange={(e) => setCreateUserLogin(e.target.checked)}
                          className="w-4 h-4 rounded border-brand-gold/20 bg-brand-smoke/50 text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                        />
                        <Label htmlFor="createUser" className="text-brand-gold cursor-pointer flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Criar usuário de login para este colaborador
                        </Label>
                      </div>

                      {createUserLogin && (
                        <div className="ml-6 space-y-4 border-l-2 border-brand-gold/20 pl-4">
                          <div>
                            <Label htmlFor="userEmail" className="text-brand-ivory">
                              Email de Login *
                            </Label>
                            <div className="relative mt-2">
                              <Input
                                id="userEmail"
                                type="email"
                                value={userEmail}
                                onChange={(e) => setUserEmail(e.target.value)}
                                placeholder="email@exemplo.com"
                                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                                required={createUserLogin}
                              />
                              <Mail className="w-4 h-4 text-brand-gold/50 absolute left-3 top-1/2 -translate-y-1/2" />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="userPassword" className="text-brand-ivory">
                              Senha *
                            </Label>
                            <div className="relative mt-2">
                              <Input
                                id="userPassword"
                                type="password"
                                value={userPassword}
                                onChange={(e) => setUserPassword(e.target.value)}
                                placeholder="Mínimo 6 caracteres"
                                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory pl-10"
                                required={createUserLogin}
                                minLength={6}
                              />
                              <Lock className="w-4 h-4 text-brand-gold/50 absolute left-3 top-1/2 -translate-y-1/2" />
                            </div>
                            <p className="text-xs text-brand-ivory/50 mt-1">
                              A senha deve ter no mínimo 6 caracteres
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddingEmployee(false)}
                        className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        onClick={handleAddEmployee}
                        className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Adicionar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Employees List */}
                <div className="border border-brand-gold/20 rounded-lg overflow-hidden">
                  {isLoadingEmployees ? (
                    <div className="p-8 text-center text-brand-ivory/50">
                      Carregando colaboradores...
                    </div>
                  ) : employees.length === 0 ? (
                    <div className="p-8 text-center text-brand-ivory/50">
                      Nenhum colaborador cadastrado
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-brand-smoke/50 border-b border-brand-gold/20">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                              Nome
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                              Cargo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                              Departamento
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                              Data de Início
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-brand-gold uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-brand-gold uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-gold/10">
                          {employees.map((employee) => (
                            <tr key={employee.id} className="hover:bg-brand-gold/5">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-brand-ivory">
                                  {employee.client.name}
                                </div>
                                {employee.client.email && (
                                  <div className="text-xs text-brand-ivory/50">
                                    {employee.client.email}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-brand-ivory">
                                  {employee.position.name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-brand-ivory">
                                  {employee.position.department || '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-brand-ivory">
                                  {new Date(employee.startDate).toLocaleDateString('pt-BR')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={cn(
                                    'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                    employee.active
                                      ? 'bg-green-900/50 text-green-400'
                                      : 'bg-red-900/50 text-red-400'
                                  )}
                                >
                                  {employee.active ? 'Ativo' : 'Inativo'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveEmployee(employee.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/empresas')}
            className="border-brand-gold/20 text-brand-ivory hover:bg-brand-gold/10"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink"
          >
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </div>
  );
}